/**
 * Runtime-configurable settings for the unattended build sessions.
 *
 * Stored in Firestore (`COLLECTION.autobuildSettings`, single doc
 * `AUTOBUILD_SETTINGS_DOC_ID`) so BOTH consumers can read the same value: the
 * admin API (Cloudflare Worker, via the Firestore REST wrapper) and the GitHub
 * Actions runner (which holds GCP_SERVICE_ACCOUNT). A repo constant or a
 * workflow env would be readable by only one of them, and a GitHub variable
 * would need admin scope the Worker does not have.
 */

/**
 * Claude accounts an OPERATOR may pick as orchestrator: `team@`, and nothing
 * else (TPF, 2026-08-26 — "team@ is to be used exclusively as orchestrator, no
 * other accounts are to be used as orchestrator").
 *
 * This list was `["claude02", "team", "tpf"]` while the orchestrator was
 * operator-configurable. `orchestratorAccount()` in `cdid-lane-assign.cjs` now
 * honours no value but `team`, so any other entry here would be a control that
 * writes a setting the runtime silently discards — the shape that let wave
 * `2026-08-23-00-58-a1` plan against an orchestrator the session was not
 * running on. The choice list is narrowed to match rather than left to
 * disagree.
 *
 * ⚠️ Reserving `team@` is exclusivity of ROLE, not of COUNT: `team@` admits as
 * many concurrent orchestrators as its admission arithmetic allows.
 *
 * Note this stays a narrower set than the account registry on purpose — it is
 * the operator-facing choice list, not the fleet. `team` remains a fully
 * registered account everywhere else (`KNOWN_ACCOUNTS`, the probe inventory,
 * the admin quota surface, the session account walk).
 */
export const ORCHESTRATOR_CANDIDATES = ["team"] as const;
export type OrchestratorAccount = (typeof ORCHESTRATOR_CANDIDATES)[number];

export const AUTOBUILD_SETTINGS_DOC_ID = "global";

export interface AutobuildSettings {
  /**
   * The account every session kind orchestrates on — planning, Karen,
   * verification, landing, reporting.
   *
   * Pinned rather than walked (TPF, 2026-08-03): orchestration must not compete
   * with implementation for one 5-hour window. Session 2026-08-03-13-31 drove
   * and built on the same account, took a 429 mid-Karen with no budget left to
   * land, and stranded six approved tickets.
   *
   * Pinned to `team` since 2026-08-26 — `cdid-lane-assign.cjs` removes `team`
   * from the worker driver pool and the worker implementation view, and honours
   * no other orchestrator value. The field survives so the stored document, the
   * admin surface and the audit line keep their shape.
   */
  orchestratorAccount: OrchestratorAccount;
  /** Who last changed it, for the audit line in the admin UI. */
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export const AUTOBUILD_SETTINGS_DEFAULTS: AutobuildSettings = {
  orchestratorAccount: "team",
  updatedBy: null,
  updatedAt: null,
};

export function isOrchestratorAccount(v: unknown): v is OrchestratorAccount {
  return typeof v === "string" && (ORCHESTRATOR_CANDIDATES as readonly string[]).includes(v);
}
